<template>
  <div>
    <!-- Alert: No item found -->
    <b-alert variant="danger" :show="userData === undefined">
      <h4 class="alert-heading">
        Error fetching user data
      </h4>
      <div class="alert-body">
        No user found with this user id. Check
        <b-link class="alert-link" :to="{ name: 'apps-users-list' }">
          User List
        </b-link>
        for other users.
      </div>
    </b-alert>

    <template v-if="userData">
      <!-- First Row -->
      <b-row>
        <b-col cols="12" xl="12" lg="12" md="12">
          <personel-view-info-card :user-data="userData" />
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" xl="12" lg="12" md="12">
          <personel-details-card :user-data="userData" />
        </b-col>
        <b-col cols="12" lg="4">
          <!-- <personel-bank-details-card :user-data="userData" /> -->
        </b-col>
      </b-row>

      <!-- Temp contract -->
      <b-row v-if="userData.tempContract && userData.tempContract.length > 0">
        <b-col cols="12" xl="12" lg="12" md="12">
          <b-card no-body>
            <div class="m-2">
              <!-- Table Top -->
              <b-row>
                <!-- Search -->
                <b-col
                  cols="12"
                  xl="12"
                  lg="12"
                  md="12"
                  :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
                >
                  <b-table
                    :items="userData.tempContract"
                    responsive
                    :fields="tableTemContractColumns"
                    show-empty
                    :empty-text="$t('no_matching_records')"
                    class="position-relative"
                    style="overflow: visible"
                  >
                    <!-- Column: Start date -->
                    <template #cell(startDate)="{ value }">
                      <span class="font-weight-bold">
                        {{
                          value.toDate().toLocaleString('fi', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        }}
                      </span>
                    </template>

                    <!-- Column: End date -->
                    <template #cell(endDate)="{ value }">
                      <span class="font-weight-bold">
                        {{
                          value.toDate().toLocaleString('fi', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        }}
                      </span>
                    </template>
                    
                    <template #cell(area)="{ index }">
                      <div>
                        <v-select
                          v-model="tempContractAreas[index]"
                          :options="tempContractAreaOptions"
                          label="name"
                          multiple
                          style="text-transform: capitalize"
                          @open="fetchAreaOptions(index)"
                          @input="onSelected(index)"
                        />
                      </div>
                    </template>

                    <template #cell(processer)="{ value }">
                      <span
                        style="text-transform: capitalize;"
                      >
                        {{value}}
                      </span>
                    </template>

                    <!-- Column: Actions -->
                    <template #cell(actions)="{ index }">
                      <b-button
                        v-if="isAbleToDelete"
                        v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                        v-b-modal.remove-temp-contract
                        size="sm"
                        variant="outline-danger"
                        @click="selectedTempContractIndex = index"
                      >
                        <feather-icon icon="XIcon" size="14" />
                      </b-button>
                    </template>
                  </b-table>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Personel Shifts -->
      <b-row>
        <b-col cols="12" xl="12" lg="12" md="12">
          <b-card no-body>
            <div class="m-2">
              <!-- Table Top -->
              <b-row>
                <!-- Search -->
                <b-col style="align-items: center; display: flex; flex-direction: row; justify-content: space-between;">
                  <div>
                    <h5>{{ `${$t(`${statusFilter.value}_shifts_total`)}: ${amount}` }}</h5>
                    <h5>{{ `${$t(`hours_total`)}: ${totalHours}` }}</h5>
                  </div>
                  <div class="d-flex">
                    <v-select
                      v-model="statusFilter"
                      :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
                      :options="statusOptions"
                      :placeholder="$t('personel.filters.status_placeholder')"
                      class="status-filter"
                      :clearable="false"
                    >
                      <template #selected-option="{ label }">
                        <span class="text-truncate overflow-hidden">
                          {{ label }}
                        </span>
                      </template>
                    </v-select>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Table Content -->
            <b-table
              ref="refShiftsUserListTable"
              :items="fetchShiftsPersonelId"
              responsive
              :fields="tableColumns"
              primary-key="id"
              show-empty
              :empty-text="$t('no_matching_records')"
              class="position-relative"
            >
              <!-- Column: title -->
              <template #cell(title)="data">
                <div class="d-flex justify-content-between align-items-center">
                  <h5 class="font-weight-bold text-primary mb-0" style="text-transform: capitalize; cursor: pointer;" @click="openShift(data.item)" >
                    {{ data.value }}
                  </h5>
                  <div>
                    <b-badge
                      v-if="data.item.boosted"
                      v-b-modal.modal-remove-boost
                      size="sm"
                      class="d-flex justify-content-between align-items-center"
                      variant="outline"
                      :style="{
                        backgroundColor:
                          data.item.boostedOptions.boostType === 'quick' ? '#48dbfb' : '#feca57',
                      }"
                      @click="shiftDeleteBoosted = data.item"
                    >
                      <feather-icon
                        :icon="
                          `${
                            data.item.boostedOptions.boostType === 'quick' ? 'ZapIcon' : 'GiftIcon'
                          }`
                        "
                      />
                      <feather-icon style="color: white" icon="XIcon" class="ml-1" />
                    </b-badge>
                  </div>
                </div>
              </template>

              <!-- Column: Start date -->
              <template #cell(start)="data">
                <span class="font-weight-bold">
                  {{
                    data.value.toDate().toLocaleString('fi', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  }}
                </span>
              </template>

              <!-- Column: End date -->
              <template #cell(end)="data">
                <span class="font-weight-bold">
                  {{
                    data.value.toDate().toLocaleString('fi', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  }}
                </span>
              </template>

              <!-- Column: Requirements -->
              <template #cell(requirements)="data">
                <b-badge
                  v-for="requirement in data.value"
                  :key="requirement"
                  variant="light-success"
                  class="mgRight-5 text-capitalize"
                >{{ requirement }}
                </b-badge>
              </template>

              <!-- Column: Status -->
              <template #cell(status)="data">
                <b-row>
                  <b-badge
                    v-if="data.item.status == 'booked' && !data.item.reserved"
                    pill
                    variant="light-warning"
                    style="text-transform: uppercase"
                  >
                    {{ $t('booked') }}
                  </b-badge>
                  <b-badge
                    v-if="data.item.status == 'booked' && data.item.reserved === true"
                    pill
                    variant="light-primary"
                    style="text-transform: uppercase"
                  >
                    {{ $t('verified') }}
                  </b-badge>
                  <b-badge
                    v-if="data.item.status == 'expired'"
                    pill
                    variant="light-danger"
                    style="text-transform: uppercase"
                  >
                    {{ $t('expired') }}
                  </b-badge>
                  <b-badge
                    v-if="data.item.status == 'confirmed'"
                    pill
                    variant="light-primary"
                    style="text-transform: uppercase"
                  >
                    {{ $t('confirmed') }}
                  </b-badge>
                  <b-badge
                    v-if="data.item.workDayType === 'sick_document_pending'"
                    pill
                    variant="light-danger"
                    style="margin-left: 10px;"
                  >
                    !
                  </b-badge>
                </b-row>
              </template>
            </b-table>

            <div class="mx-2 mb-2">
              <b-row>
                <!-- Load More -->
                <b-col
                  cols="12"
                  sm="12"
                  class="d-flex align-items-center justify-content-center justify-content-sm-center"
                >
                  <b-button
                    v-show="canLoadMore"
                    v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                    pill
                    :disabled="loadMore"
                    variant="outline-secondary"
                    @click="onLoadMore"
                  >
                    <div v-if="loadMore">
                      <b-spinner small />
                      {{ $t('loading') }}
                    </div>
                    <div v-if="!loadMore">
                      {{ $t('load_more') }}
                    </div>
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" xl="12" lg="12" md="12">
          <personel-block-card
            v-if="userData.blockedFrom && userData.blockedFrom.length > 0"
            :user-data="userData"
          />
        </b-col>
      </b-row>
    </template>
    <manage-shift-sidebar
      v-model="isEventHandlerSidebarActive"
      :event="event"
      @onUpdated="updateData"
    />
    <b-modal
      id="modal-remove-boost"
      ok-only
      ok-variant="danger"
      ok-title="Poista"
      modal-class="modal-danger"
      centered
      title="Poista boosti"
      @ok="confirmDelete"
    >
      <b-card-text>
        Olet poistamassa boostin vuorosta, tätä toimintoa ei voi perua.
      </b-card-text>
    </b-modal>
    <b-modal
      id="remove-temp-contract"
      ok-only
      ok-variant="danger"
      ok-title="Remove"
      modal-class="modal-danger"
      centered
      title="Remove temporary contract"
      @ok="removeTempContract"
      @hide="selectedTempContractIndex = -1"
    >
      <b-card-text>
        Do you want to remove this temporary contract?
      </b-card-text>
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge,
  BRow,
  BCol,
  BAlert,
  BLink,
  BCard,
  BTable,
  BButton,
  BSpinner,
  BModal,
  BCardText,
} from 'bootstrap-vue'
import i18n from '@hw-core/libs/i18n'
import vSelect from 'vue-select'
import { db } from '@/shared/services/firebase'
import Ripple from 'vue-ripple-directive'
import PersonelViewInfoCard from './PersonelViewInfoCard.vue'
import PersonelDetailsCard from './PersonelDetailsCard.vue'
// import PersonelBankDetailsCard from './PersonelBankDetailsCard.vue'
import PersonelBlockCard from './PersonelViewBlockCard.vue'
// eslint-disable-next-line import/no-cycle
import usePersonelView from './usePersonelView'
import ManageShiftSidebar from '../../../1-shifts-&-gigs/shifts-calendar/calendar-event-handler/ManageShiftSidebar.vue'

export default {
  components: {
    BBadge,
    BRow,
    BCol,
    BAlert,
    BLink,
    BCard,
    BCardText,
    BModal,
    BTable,
    BButton,
    BSpinner,

    // Local Components
    PersonelViewInfoCard,
    PersonelDetailsCard,
    PersonelBlockCard,
    ManageShiftSidebar,
    // PersonelBankDetailsCard,

    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      processer: {},
      tempContractAreas: [],
      tempContractAreaOptions: [],
      tempContract: [],
    }
  },
  computed: {
    isAbleToDelete() {
      return (
        this.processer.email === 'ilmi@hoiwa.fi' || this.processer.email === 'lari.juva@hoiwa.fi'
      )
    },
  },
  updated() {
    if (this.userData.tempContract && this.userData.tempContract[0].area !== undefined) {
      this.update()
    }
  },
  beforeCreate() {
    this.$store.dispatch('appAuth/getCurrentUser').then(res => {
      const user = JSON.parse(JSON.stringify(res))
      this.processer = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      }
    })
  },
  methods: {
    onLoadMore() {
      this.currentPage += 1
      this.loadMore = true
    },
    fetchAreaOptions(index) {
      const selectedTempContract = this.tempContract[index]
      this.tempContractAreaOptions = selectedTempContract.areaOptions.filter(item => item.id !== selectedTempContract.areaId)
    },
    onSelected(index) {
      const updatedSecondaryAreas = this.tempContractAreas[index].map(item => item.id)
      const selectedTempContract = this.tempContract[index]

      const updatedTempContracts = this.tempContract.map(obj => ({
        accountId: obj.accountId,
        areaId: obj.areaId,
        boost: obj.boost === undefined ? {} : obj.boost,
        contractId: obj.contractId,
        endDate: obj.endDate,
        processer: obj.processer,
        // eslint-disable-next-line no-nested-ternary
        secondaryAreas: obj === selectedTempContract ? updatedSecondaryAreas : obj.secondaryAreas === undefined ? [] : obj.secondaryAreas,
        startDate: obj.startDate,
        workContract: obj.workContract,
      }))
      db.collection('users').doc(this.userData.id).update({
        tempContract: updatedTempContracts
      })

      const localTempContractsData = this.tempContract.map(obj => ({
        ...obj,
        boost: obj.boost === undefined ? {} : obj.boost,
        // eslint-disable-next-line no-nested-ternary
        secondaryAreas: obj === selectedTempContract ? updatedSecondaryAreas : obj.secondaryAreas === undefined ? [] : obj.secondaryAreas,
      }))
      this.tempContract = localTempContractsData

      this.update()
    },
    async update() {
      if (this.tempContract.length < 1) {
        this.tempContract = this.userData.tempContract
      }
      const secondaryAreaIds = this.tempContract.map(item => item.secondaryAreas)

      // eslint-disable-next-line no-restricted-syntax, no-plusplus
      for (let i = 0; i < secondaryAreaIds.length; i++) {
        if (secondaryAreaIds[i] === undefined) {
          this.tempContractAreas = [...this.tempContractAreas, []]
        } else {
          // eslint-disable-next-line no-await-in-loop
          const secondaryAreas = await Promise.all(secondaryAreaIds[i].map(async item => {
            const areaId = JSON.stringify(item).replaceAll('"', '')
            const areaDoc = await db.collection('areas').doc(areaId).get()
            
            return {
              ...areaDoc.data(),
              id: areaDoc.id,
            }
          }))
          this.tempContractAreas = [...this.tempContractAreas, secondaryAreas]
        }
      }
    }
  },
  setup() {

    const {
      userData,
      statusFilter,
      tableColumns,
      currentPage,
      canLoadMore,
      loadMore,
      amount,
      totalHours,
      fetchUser,
      fetchShiftsPersonelId,
      refShiftsUserListTable,
      shiftDeleteBoosted,
      confirmDelete,
      removeTempContract,
      tableTemContractColumns,
      selectedTempContractIndex,
      openShift,
      isEventHandlerSidebarActive,
      event,
      refetchData,
      updateData,
    } = usePersonelView()

    // Fetch user data
    fetchUser()
    const statusOptions = [
      { label: i18n.t('personel.filters.booked'), value: 'booked' },
      { label: i18n.t('personel.filters.verified'), value: 'verified' },
      { label: i18n.t('personel.filters.confirmed'), value: 'confirmed' },
      { label: i18n.t('personel.filters.expired'), value: 'expired' },
    ]

    return {
      userData,
      statusFilter,
      statusOptions,
      tableColumns,
      currentPage,
      canLoadMore,
      amount,
      totalHours,
      loadMore,
      fetchUser,
      fetchShiftsPersonelId,
      refShiftsUserListTable,
      shiftDeleteBoosted,
      confirmDelete,
      removeTempContract,
      selectedTempContractIndex,
      tableTemContractColumns,
      openShift,
      isEventHandlerSidebarActive,
      event,
      refetchData,
      updateData,
    }
  },
}
</script>

<style>
.status-filter {
  min-width: 150px;
}
.mgRight-5 {
  margin-right: 3px;
}
#dropdown-menu .dropdown-toggle {
  padding-top: 0;
  padding-bottom: 0;
}
</style>

<style scoped>
.btn-delete {
  width: 30px;
  height: 30px;
  padding: 5px;
}
</style>
